import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation, useInView } from 'framer-motion';

const Home = () => {
  const [counts, setCounts] = useState({ tonnes: 0, clients: 0, countries: 0, years: 0 });

  // Animated counter effect
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    const targets = { tonnes: 100000, clients: 50, countries: 10, years: 3 };
    let step = 0;

    const timer = setInterval(() => {
      step++;
      setCounts({
        tonnes: Math.floor((targets.tonnes / steps) * step),
        clients: Math.floor((targets.clients / steps) * step),
        countries: Math.floor((targets.countries / steps) * step),
        years: Math.floor((targets.years / steps) * step),
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const commodities = [
    {
      title: 'Fertiliser (Potash MOP)',
      subtitle: 'Agricultural Excellence',
      description: 'Premium Muriate of Potash (MOP) fertiliser with 60-62% K₂O content. Sourced from certified suppliers, delivered across three continents.',
      icon: '🌱',
      gradient: 'from-emerald-500 via-green-500 to-teal-500',
      stats: '100K+ tonnes annually',
      features: ['60-62% K₂O Content', 'Bulk & Containerized', 'Full Certification'],
    },
    {
      title: 'Thermal Coal',
      subtitle: 'Energy & Industry',
      description: 'Quality thermal coal for power generation and industrial applications. Various calorific values available with reliable supply chains.',
      icon: '⚡',
      gradient: 'from-slate-600 via-gray-600 to-zinc-700',
      stats: '200K+ tonnes capacity',
      features: ['Multiple CV Options', 'Low Ash Content', 'ISO Compliant'],
    },
    {
      title: 'Wheat',
      subtitle: 'Food Security',
      description: 'Premium wheat grades for milling and feed applications. Supporting global food security with quality-tested grain.',
      icon: '🌾',
      gradient: 'from-amber-500 via-yellow-500 to-orange-500',
      stats: '150K+ tonnes traded',
      features: ['Milling & Feed Grade', 'Phytosanitary Certified', 'Flexible Delivery'],
    },
    {
      title: 'Titanium & Metals',
      subtitle: 'Industrial Materials',
      description: 'Specialized metals supply for aerospace, manufacturing, and industrial sectors. Multiple grades and forms available.',
      icon: '⚙️',
      gradient: 'from-blue-600 via-indigo-600 to-purple-600',
      stats: 'Bespoke sourcing',
      features: ['Various Grades', 'Full Traceability', 'Technical Support'],
    },
  ];

  const services = [
    {
      icon: '🚢',
      title: 'Global Logistics',
      description: 'End-to-end supply chain management from origin to destination',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: '✓',
      title: 'Quality Assurance',
      description: 'Independent testing and certification to international standards',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: '📊',
      title: 'Market Intelligence',
      description: 'Real-time pricing and market insights for informed decisions',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: '🤝',
      title: 'Custom Solutions',
      description: 'Tailored sourcing and supply based on specific requirements',
      color: 'from-orange-500 to-red-500',
    },
  ];

  const regions = [
    { 
      name: 'Europe', 
      icon: '🇪🇺', 
      description: 'Comprehensive supply network across EU markets',
      markets: ['UK', 'Germany', 'France', 'Netherlands', 'Spain']
    },
    { 
      name: 'Asia', 
      icon: '🌏', 
      description: 'Strategic partnerships in high-growth Asian economies',
      markets: ['India', 'China', 'UAE', 'Pakistan', 'Bangladesh']
    },
    { 
      name: 'Africa', 
      icon: '🌍', 
      description: 'Supporting development with reliable commodity supply',
      markets: ['South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco']
    },
  ];

  const process = [
    { step: '01', title: 'Inquiry', description: 'Share your commodity requirements and specifications' },
    { step: '02', title: 'Sourcing', description: 'We identify and verify suppliers from our global network' },
    { step: '03', title: 'Quality Check', description: 'Independent testing and certification verification' },
    { step: '04', title: 'Logistics', description: 'Comprehensive shipping and customs coordination' },
    { step: '05', title: 'Delivery', description: 'On-time delivery with full documentation and support' },
  ];

  const testimonials = [
    {
      quote: "Markhor Group's reliability in supplying Potash MOP has been exceptional. Their attention to quality and timely delivery makes them our preferred partner.",
      author: "Agricultural Imports Ltd",
      location: "Netherlands",
      rating: 5,
    },
    {
      quote: "We've been sourcing thermal coal through Markhor for 3 years. Their market knowledge and competitive pricing consistently deliver value.",
      author: "Energy Solutions GmbH",
      location: "Germany",
      rating: 5,
    },
    {
      quote: "Professional service from inquiry to delivery. Their logistics coordination and documentation support is outstanding.",
      author: "Global Commodities Trading",
      location: "UAE",
      rating: 5,
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Animated Background */}
      <section className="relative bg-gradient-to-br from-primary via-primary-dark to-accent text-white pt-32 pb-24 overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <motion.div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
            animate={{
              backgroundPosition: ['0px 0px', '60px 60px'],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-secondary rounded-full opacity-30"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Global Commodity Trading
              <br />
              <span className="text-secondary bg-clip-text text-transparent bg-gradient-to-r from-secondary via-secondary-light to-secondary">
                Done Right
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl mb-8 text-gray-200 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Reliable supply of <span className="text-secondary font-semibold">Fertilisers</span>, <span className="text-secondary font-semibold">Thermal Coal</span>, <span className="text-secondary font-semibold">Wheat</span>, and <span className="text-secondary font-semibold">Metals</span> across Europe, Asia, and Africa
            </motion.p>

            <motion.p
              className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Professional commodity trading with comprehensive logistics support. We handle everything from sourcing to delivery, so you can focus on your business.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Link
                to="/contact"
                className="group bg-secondary hover:bg-secondary-dark text-primary font-bold px-10 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <span className="flex items-center justify-center">
                  Request a Quote
                  <motion.span
                    className="ml-2"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </span>
              </Link>
              <Link
                to="/products"
                className="border-2 border-white hover:bg-white hover:text-primary font-semibold px-10 py-4 rounded-lg transition-all duration-300 backdrop-blur-sm bg-white/10"
              >
                View Commodities
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="#f9fafb"
            />
          </svg>
        </div>
      </section>

      {/* Animated Stats Counter */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: counts.tonnes.toLocaleString(), suffix: '+', label: 'Tonnes Handled Annually', icon: '📦' },
              { value: counts.clients, suffix: '+', label: 'Active Clients', icon: '🤝' },
              { value: counts.countries, suffix: '+', label: 'Countries Served', icon: '🌍' },
              { value: counts.years, suffix: '+', label: 'Years Combined Experience', icon: '⭐' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="text-4xl mb-3">{stat.icon}</div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.value}<span className="text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Commodities - Enhanced */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-primary mb-4">Our Core Commodities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specializing in essential commodities with proven track records and reliable supply chains
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {commodities.map((commodity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${commodity.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl`} />
                
                <div className="relative bg-white border-2 border-gray-100 group-hover:border-secondary/30 rounded-2xl p-8 shadow-lg group-hover:shadow-2xl transition-all duration-300">
                  {/* Icon and Badge */}
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-br ${commodity.gradient} rounded-2xl flex items-center justify-center text-4xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      {commodity.icon}
                    </div>
                    <span className="px-4 py-2 bg-secondary/10 text-secondary text-sm font-semibold rounded-full">
                      {commodity.stats}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-primary mb-2">
                    {commodity.title}
                  </h3>
                  <p className="text-secondary font-semibold mb-4">
                    {commodity.subtitle}
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {commodity.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2">
                    {commodity.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <span className="text-secondary mr-2">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Hover Arrow */}
                  <motion.div
                    className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <span className="text-secondary text-3xl">→</span>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/products"
              className="inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors duration-300"
            >
              View Detailed Specifications
              <motion.span
                className="ml-2"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                →
              </motion.span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Beyond Supply</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive support services for seamless commodity trading
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-xl`} />
                
                <div className="relative bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold text-primary mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach - Enhanced */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Global Reach, Local Expertise</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Serving clients across three continents with established supply networks
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {regions.map((region, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="text-6xl mb-4 text-center">{region.icon}</div>
                <h3 className="text-2xl font-bold text-primary mb-3 text-center">{region.name}</h3>
                <p className="text-gray-600 mb-6 text-center">{region.description}</p>
                
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-secondary mb-2">Key Markets:</p>
                  {region.markets.map((market, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2" />
                      {market}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gradient-to-br from-primary to-accent text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">How We Work</h2>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              A streamlined process designed for efficiency and transparency
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-white/20" />

            <div className="grid md:grid-cols-5 gap-6">
              {process.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-2xl font-bold text-primary mb-4 mx-auto shadow-xl">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-center">{item.title}</h3>
                    <p className="text-gray-200 text-sm text-center">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Trusted by Leading Companies</h2>
            <p className="text-xl text-gray-600">
              What our clients say about working with us
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                {/* Stars */}
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-secondary text-xl">★</span>
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>

                {/* Author */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-primary">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h3 className="text-2xl font-bold text-primary mb-8">Our Commitments</h3>
            <div className="flex flex-wrap justify-center gap-8">
              {[
                { icon: '✓', label: 'Quality Certified' },
                { icon: '🔒', label: 'Secure Transactions' },
                { icon: '🌱', label: 'Sustainable Sourcing' },
                { icon: '📋', label: 'Full Documentation' },
                { icon: '🤝', label: 'Trusted Partnerships' },
                { icon: '⚡', label: 'Fast Delivery' },
              ].map((badge, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.1 }}
                  className="flex flex-col items-center p-4"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-2xl mb-2 text-white shadow-lg">
                    {badge.icon}
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{badge.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary via-primary-dark to-accent text-white relative overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10"
        >
          <h2 className="text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-10 text-gray-200 leading-relaxed">
            Get in touch today to discuss your commodity requirements. Our team is ready to provide competitive quotes and expert guidance.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="group bg-secondary hover:bg-secondary-dark text-primary font-bold px-12 py-5 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <span className="flex items-center justify-center">
                Request a Quote
                <motion.span
                  className="ml-2"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
            </Link>
            <Link
              to="/about"
              className="border-2 border-white hover:bg-white hover:text-primary font-semibold px-12 py-5 rounded-lg transition-all duration-300 backdrop-blur-sm"
            >
              Learn More About Us
            </Link>
          </div>

          <div className="mt-12 pt-12 border-t border-white/20">
            <p className="text-gray-300 mb-4">Contact us directly:</p>
            <a href="mailto:info@markhor-group.com" className="text-secondary hover:text-secondary-light text-xl font-semibold transition-colors duration-300">
              info@markhor-group.com
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Home;